import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, forwardRef, inject, Input, OnInit, Output } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { AccessDataService } from '@adm-web-app/modules/access/services/access-data.service';
import { NodeKind, NodeRef, TreeNodeModel } from '@adm-web-app/modules/access/models/access.models';
import { AccessTitlePipe } from '@adm-web-app/modules/access/pipes/access.pipes';
import { titleIn } from '@adm-web-app/modules/access/util/access-format';
import { ICONS } from '@adm-web-app/modules/access/util/access-icons';
import { ancestorsOf, buildTreeIndex, buildTreeRows, expandPathOf, TreeIndex, TreeRowView } from '@adm-web-app/modules/access/util/access-tree';

interface PathPart { id: number; title: string }
interface SelectRow extends TreeRowView { allowed: boolean; why: string }

/**
 * The select of a place in the org tree (`GET /api/vn/adm/access/tree`): the field shows the path of the node, the panel is the tree with a search. Used by the dialogs of
 * «Доступы» and by the form of a store («Место в дереве»). `[allowed]` says which nodes can be chosen, the others are shown grey.
 */
@Component({
  selector: 'hc-tree-node-select',
  templateUrl: './tree-node-select.component.html',
  styleUrls: ['./tree-node-select.component.less'],
  imports: [FormsModule, CdkConnectedOverlay, CdkOverlayOrigin, AccessTitlePipe],
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => TreeNodeSelectComponent), multi: true }]
})
export class TreeNodeSelectComponent implements OnInit, ControlValueAccessor {
  @Input() placeholder = '';
  @Input() searchPlaceholder = '';
  @Input() emptyText = '';
  @Input() invalid = false;
  /** Nodes that can be chosen; the default is every live node. */
  @Input() allowed: (node: TreeNodeModel) => boolean = () => true;
  /** Why a node that is shown grey cannot be chosen (a short phrase, shown on the row and on hover); empty — no reason to tell. */
  @Input() reasonOf: (node: TreeNodeModel) => string = () => '';
  /** Kinds of nodes that can be chosen, a shortcut for the common case. */
  @Input() kinds: readonly NodeKind[] = ['G', 'D', 'S'];
  @Input() ariaLabel = '';
  /** A borderless field inside a filter pill (the filters of the journal). */
  @Input() inline = false;
  /** A small badge at the right of the field (the dialog Grant: «место работы»). */
  @Input() badge = '';
  @Output() readonly chosen = new EventEmitter<NodeRef>();

  protected value: number | null = null;
  protected open = false;
  protected disabled = false;
  protected width = 0;
  protected loading = true;
  protected failed = false;
  protected query = '';
  protected path: PathPart[] = [];
  protected rows: SelectRow[] = [];
  protected readonly chevron = 'M6 9l6 6 6-6';
  protected readonly iconPaths: Record<string, string> = { globe: ICONS.globe, flag: ICONS.flag, city: ICONS.city, group: ICONS.group, dept: ICONS.dept, store: ICONS.store };

  private readonly data = inject(AccessDataService);
  private readonly tr = inject(TranslocoService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private index: TreeIndex | null = null;
  private expanded: Record<number, boolean> = {};
  private showAll: Record<number, boolean> = {};
  private onChange: (value: number | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;


  ngOnInit(): void {
    void this.load();
  }

  writeValue(value: number | null): void {
    this.value = value ?? null;
    this.refreshPath();
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  protected toggle(): void {
    if (this.disabled) {
      return;
    }
    this.open = !this.open;
    if (this.open) {
      this.width = this.host.nativeElement.getBoundingClientRect().width;
      this.query = '';
      this.rebuild(true);
    } else {
      this.onTouched();
    }
  }

  protected close(): void {
    this.open = false;
    this.onTouched();
  }

  protected onQuery(): void {
    this.rebuild(false);
  }

  protected toggleRow(event: Event, row: SelectRow): void {
    event.stopPropagation();
    this.expanded = { ...this.expanded, [row.id]: !this.expanded[row.id] };
    this.rebuild(false);
  }

  protected more(event: Event, row: SelectRow): void {
    event.preventDefault();
    this.showAll = { ...this.showAll, [row.id]: true };
    this.rebuild(false);
  }

  protected pick(row: SelectRow): void {
    if (row.kind !== 'node' || !row.node || !row.allowed) {
      return;
    }
    this.value = row.node.id;
    this.onChange(row.node.id);
    this.chosen.emit({ id: row.node.id, title: row.node.title, kind: row.node.kind });
    this.refreshPath();
    this.close();
  }

  protected isAllowed(node: TreeNodeModel): boolean {
    return this.kinds.includes(node.kind) && this.allowed(node);
  }

  private rowAllowed(row: TreeRowView): boolean {
    return row.kind === 'node' && !!row.node && this.isAllowed(row.node);
  }

  private async load(): Promise<void> {
    try {
      const model = await this.data.tree();
      this.index = buildTreeIndex(model, this.tr.getActiveLang());
      this.loading = false;
      this.refreshPath();
    } catch {
      this.failed = true;
      this.loading = false;
    }
    this.cdr.markForCheck();
  }

  private refreshPath(): void {
    const index = this.index;
    if (!index || this.value === null) {
      this.path = [];
      return;
    }
    const lang = this.tr.getActiveLang();
    const node = index.byId.get(this.value);
    this.path = node ? [...ancestorsOf(index, node.id), node].map(n => ({ id: n.id, title: titleIn(n.title, lang) })) : [];
  }

  private rebuild(expandToValue: boolean): void {
    const index = this.index;
    if (!index) {
      return;
    }
    if (expandToValue) {
      this.expanded = expandPathOf(index, this.value, this.expanded);
      if (this.value === null && index.roots.length > 0) {
        this.expanded = { ...this.expanded, [index.roots[0]]: true };
      }
    }
    this.rows = buildTreeRows(index, { expanded: this.expanded, selectedId: this.value, query: this.query, showAll: this.showAll, lang: this.tr.getActiveLang() })
      .map(row => {
        const allowed = this.rowAllowed(row);
        return { ...row, allowed, why: allowed || !row.node ? '' : this.reasonOf(row.node) };
      });
  }
}
